import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import FocusLock from 'react-focus-lock';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ConfigurationPanelStepBackIcon} from '@amblique/king-living/app/components/configuration-selector/configuration-panel-step-back-icon';
import ReviewSection from '@amblique/king-living/app/components/configuration-selector/review-section';
import {CoverStep} from '@amblique/king-living/app/components/complex-customize/cover-step';
import {SubtotalFooter} from '@amblique/king-living/app/components/complex-customize/option-step';
import {sumSelectionPrices} from '@amblique/king-living/app/components/complex-customize/utils/selection-utils';
import {
  enrichSimplePdpCoverFlyoutSelection,
  getSimplePdpCoverFlyoutEffectivePrice,
  getSimplePdpCoverFlyoutListPrice,
  hasCustomizeOptionSelections,
  isCoverVariantBookPriceMissing,
  resolveCoverCatalogVariantPricing,
} from '@amblique/king-living/app/components/cover-swatch-grid/simple-pdp-cover-flyout-pricing';
import {PDP_DESKTOP_CONFIG_PANEL_WIDTH_PX} from '@amblique/king-living/app/constants/pdp-layout';
import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {useVisualViewportRect} from '@amblique/king-living/app/hooks/use-visual-viewport-rect';
import {usePdpConfigPanelA11y, focusPdpPanelInitialTarget} from '@amblique/king-living/app/hooks/use-pdp-config-panel-a11y';
import {useMobilePdpDrawerScrollLock} from '@amblique/king-living/app/hooks/use-mobile-pdp-drawer-scroll-lock';
import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {
  computeCustomizeListTotal,
  computeCustomizeTotal,
  resolveKingGuardSourceProduct,
} from '@amblique/king-living/app/utils/cart-utils';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';

function findVariantByProductId(variants, productId) {
  if (!productId || !Array.isArray(variants)) return null;

  return variants.find((variant) => variant.productId === productId) ?? null;
}

/**
 * Simple PDP cover overflow flyout — opens from CoverSwatchGrid "+N more".
 * Reuses Complex PDP CoverStep UI; footer confirms with Done (not Next).
 */
export function CoverSwatchOverflowFlyout({
  atcDisplayPrice,
  committedCover,
  committedSelections,
  coverGroups,
  currency,
  isOpen,
  isSimpleConfigurableMaster = false,
  masterMinPrice,
  mobileGalleryLayers,
  mobileGalleryPreviewProduct,
  mobileGalleryVariationAttributes,
  optionListPriceMap,
  optionPriceMap,
  product,
  variants,
  onClose,
  onCommit,
  onGalleryPreviewChange,
  onViewDetails,
}) {
  const intl = useIntl();
  const isMobileBreakpoint = useBreakpointValue({base: true, lg: false});
  const isMobile = isMobileBreakpoint === true;
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const drawerViewportRect = useVisualViewportRect(isOpen && isMobile);
  const {handleDrawerCloseComplete} = useMobilePdpDrawerScrollLock(isOpen, isMobile);
  const backButtonRef = useRef(null);
  const hasUserPreviewedCoverRef = useRef(false);
  const [draftSelections, setDraftSelections] = useState({cover: committedCover ?? null});

  useEffect(() => {
    if (!isOpen) {
      hasUserPreviewedCoverRef.current = false;

      return;
    }

    setDraftSelections({cover: committedCover ?? null});
  }, [committedCover, isOpen]);

  const draftCoverId = draftSelections?.cover?.productId ?? null;

  const draftCoverVariant = useMemo(
    () => findVariantByProductId(variants, draftCoverId),
    [draftCoverId, variants]
  );

  const {data: draftCoverProductResult} = useProducts(
    {parameters: {ids: draftCoverId ?? '', ...GET_PRODUCTS_PARAMS}},
    {enabled: Boolean(isOpen && draftCoverId)}
  );

  const draftCoverProduct = useMemo(() => {
    if (!draftCoverId) return null;

    const fetched = draftCoverProductResult?.data?.[0];

    if (!fetched) return null;

    const fetchedId = fetched.id ?? fetched.productId;

    return fetchedId === draftCoverId ? fetched : null;
  }, [draftCoverId, draftCoverProductResult]);

  useEffect(() => {
    if (!draftCoverProduct || !draftCoverId) return;

    setDraftSelections((prev) => {
      const cover = prev?.cover;

      if (!cover || cover.productId !== draftCoverId) return prev;

      const enriched = enrichSimplePdpCoverFlyoutSelection(cover, draftCoverProduct, {
        promoOnly: isSimpleConfigurableMaster,
      });

      if (enriched === cover) return prev;

      return {...prev, cover: enriched};
    });
  }, [draftCoverId, draftCoverProduct, isSimpleConfigurableMaster]);

  useEffect(() => {
    if (!isOpen || !hasUserPreviewedCoverRef.current || !draftCoverId || !draftCoverProduct) return;

    onGalleryPreviewChange?.({
      coverProductId: draftCoverId,
      product,
      selectedVariationAttributes: draftCoverVariant?.variationValues ?? {},
      viewDetailsProduct: draftCoverProduct,
    });
  }, [
    draftCoverId,
    draftCoverProduct,
    draftCoverVariant,
    isOpen,
    onGalleryPreviewChange,
    product
  ]);

  const footerProduct = useMemo(() => {
    const cover = draftSelections?.cover;

    if (!cover?.productId) return null;

    return {
      imageUrl: cover.imageUrl ?? null,
      name: cover.name ?? '',
    };
  }, [draftSelections?.cover]);

  const hasCommittedOptions = useMemo(
    () => hasCustomizeOptionSelections(committedSelections),
    [committedSelections]
  );

  const pricingSelections = useMemo(() => {
    const cover = draftSelections?.cover;
    const base =
      committedSelections && typeof committedSelections === 'object' ? committedSelections : {};

    if (!cover?.productId) {
      return {...base, cover: cover ?? base.cover ?? null};
    }

    const variant = findVariantByProductId(variants, cover.productId);

    if (
      isSimpleConfigurableMaster &&
      isCoverVariantBookPriceMissing(variant) &&
      hasCommittedOptions
    ) {
      return {
        ...base,
        cover: {...cover, salePrice: 0, price: null},
      };
    }

    const catalogPricing = resolveCoverCatalogVariantPricing(variant);
    const coverSalePrice = cover.salePrice ?? catalogPricing.salePrice ?? null;
    const coverPrice = cover.price ?? catalogPricing.price ?? null;
    const resolvedCover =
      cover.salePrice === coverSalePrice && cover.price === coverPrice ?
        cover :
        {...cover, salePrice: coverSalePrice, price: coverPrice};

    return {
      ...base,
      cover: resolvedCover,
    };
  }, [committedSelections, draftSelections, hasCommittedOptions, isSimpleConfigurableMaster, variants]);

  const kingGuardSource = useMemo(
    () => resolveKingGuardSourceProduct(product, draftCoverVariant),
    [draftCoverVariant, product]
  );

  const usePackageVariantPricing = !isSimpleConfigurableMaster;

  const draftCoverPriceMap = useMemo(() => {
    if (!usePackageVariantPricing) {
      return optionPriceMap ?? null;
    }

    if (!draftCoverId) return optionPriceMap ?? null;

    const effectivePrice = draftCoverProduct ?
      getSimplePdpCoverFlyoutEffectivePrice(draftCoverProduct) :
      optionPriceMap?.[draftCoverId] ??
      getSimplePdpCoverFlyoutEffectivePrice(draftCoverVariant) ??
      null;

    if (effectivePrice == null) return optionPriceMap ?? null;

    return {
      ...(optionPriceMap ?? {}),
      [draftCoverId]: effectivePrice,
    };
  }, [draftCoverId, draftCoverProduct, draftCoverVariant, optionPriceMap, usePackageVariantPricing]);

  const draftCoverListPriceMap = useMemo(() => {
    if (!usePackageVariantPricing || !draftCoverId) return null;

    const listPrice = draftCoverProduct ?
      getSimplePdpCoverFlyoutListPrice(draftCoverProduct) :
      getSimplePdpCoverFlyoutListPrice(draftCoverVariant);

    if (listPrice == null) return null;

    return {[draftCoverId]: listPrice};
  }, [draftCoverId, draftCoverProduct, draftCoverVariant, usePackageVariantPricing]);

  const packageListPriceMap = useMemo(() => {
    if (!usePackageVariantPricing) return optionListPriceMap ?? null;

    if (!draftCoverListPriceMap && !optionListPriceMap) return optionListPriceMap ?? null;

    return {
      ...(optionListPriceMap ?? {}),
      ...(draftCoverListPriceMap ?? {}),
    };
  }, [draftCoverListPriceMap, optionListPriceMap, usePackageVariantPricing]);

  const subtotalPrice = useMemo(() => {
    if (!pricingSelections?.cover?.productId) return null;

    const promoAwareTotal = computeCustomizeTotal(
      pricingSelections,
      product,
      true,
      kingGuardSource,
      draftCoverPriceMap
    );

    if (promoAwareTotal > 0) {
      return promoAwareTotal;
    }

    const saleBookTotal = sumSelectionPrices(pricingSelections);

    if (saleBookTotal != null && saleBookTotal > 0) {
      return saleBookTotal;
    }

    return atcDisplayPrice ?? masterMinPrice ?? 0;
  }, [
    atcDisplayPrice,
    draftCoverPriceMap,
    kingGuardSource,
    masterMinPrice,
    pricingSelections,
    product
  ]);

  const subtotalListPrice = useMemo(
    () => computeCustomizeListTotal(
      pricingSelections,
      product,
      true,
      kingGuardSource,
      packageListPriceMap
    ),
    [kingGuardSource, packageListPriceMap, pricingSelections, product]
  );

  const showListPrice = subtotalListPrice > 0 && subtotalListPrice > (subtotalPrice ?? 0);

  const priceText = subtotalPrice != null ? (
    <Flex alignItems="baseline" gap={1} wrap="wrap">
      <FormattedMessage
        defaultMessage="Package price {price}"
        id="customize_panel.footer.subtotal"
        values={{
          price: formatCurrencyHideTrailingZeroCents(intl, subtotalPrice, currency),
        }}
      />
      {showListPrice && (
        <Text as="span" color="greyscale.70" fontSize="xs" textDecoration="line-through">
          {formatCurrencyHideTrailingZeroCents(intl, subtotalListPrice, currency)}
        </Text>
      )}
    </Flex>
  ) : null;

  const previewGallery = useCallback((coverSelection) => {
    const variant = findVariantByProductId(variants, coverSelection?.productId);

    if (!variant) return;

    onGalleryPreviewChange?.({
      coverProductId: coverSelection?.productId ?? null,
      product,
      selectedVariationAttributes: variant.variationValues ?? {},
      viewDetailsProduct: variant,
    });
  }, [onGalleryPreviewChange, product, variants]);

  const handleCoverSelect = useCallback((stepId, value) => {
    if (stepId !== 'cover') return;

    hasUserPreviewedCoverRef.current = true;
    setDraftSelections({cover: value});
    previewGallery(value);
  }, [previewGallery]);

  const handleCoverViewDetails = useCallback((variant, viewOptions = {}) => {
    onViewDetails?.(variant, viewOptions);
  }, [onViewDetails]);

  const handleDone = useCallback(() => {
    const variant = findVariantByProductId(variants, draftSelections?.cover?.productId);

    if (variant) {
      onCommit?.(variant);
    }

    onClose?.();
  }, [draftSelections?.cover?.productId, onClose, onCommit, variants]);

  const handleDismiss = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const {panelRef: desktopPanelRef} = usePdpConfigPanelA11y({
    focusKey: isOpen,
    initialFocusRef: backButtonRef,
    isActive: isOpen && !isMobile,
    onDismiss: handleDismiss,
  });

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    focusPdpPanelInitialTarget(backButtonRef, isMobile ? null : desktopPanelRef);

    return undefined;
  }, [desktopPanelRef, isMobile, isOpen]);

  const mobileGalleryHeader = isMobile ? (
    <Box {...styles.mobileImageHeader} className="cover-swatch-overflow-flyout__mobile-gallery">
      <ReviewSection
        layers={mobileGalleryLayers}
        masterProduct={product}
        selectedVariationAttributes={mobileGalleryVariationAttributes ?? {}}
        showViewDetailsSection={false}
        previewProduct={
          mobileGalleryPreviewProduct && mobileGalleryPreviewProduct !== product ?
            mobileGalleryPreviewProduct :
            null
        }
        forceCylindoCheck
        isSimpleProduct
        isThumbnailZoomEnabled
      />
    </Box>
  ) : null;

  const panelBody = (
    <>
      <Box
        className="customize-step-cover-content-body cover-swatch-overflow-flyout__body"
        flex={1}
        minH={0}
        overflowY="auto"
        paddingX={{base: 4, lg: 6}}
        paddingY={4}
        sx={{WebkitOverflowScrolling: 'touch', touchAction: 'pan-y'}}
        {...styles.body}
      >
        <CoverStep
          coverGroups={coverGroups}
          currency={currency}
          isMobile={isMobile}
          masterMinPrice={masterMinPrice}
          selection={draftSelections}
          isSimplePDPCoverFlyout
          onSelect={handleCoverSelect}
          onViewDetails={handleCoverViewDetails}
        />
      </Box>
      <SubtotalFooter
        emptyTitleContext="nestedSelection"
        footerProduct={footerProduct}
        priceText={priceText}
        styles={styles}
      >
        <Button
          {...styles.subtotalDoneButton}
          aria-disabled={!footerProduct}
          id="cover-overflow-done-btn"
          isDisabled={!footerProduct}
          onClick={handleDone}
        >
          <FormattedMessage defaultMessage="Done" id="customize_panel.done_button" />
        </Button>
      </SubtotalFooter>
    </>
  );

  const headerTitle = (
    <Flex {...styles.panelHeaderTitleRow} className="panel-header-title-row" width="100%">
      <Text
        className="panel-header-title"
        id="cover-overflow-flyout-title"
        {...styles.panelHeaderTitleLabel}
        noOfLines={1}
      >
        <FormattedMessage
          defaultMessage="Choose your cover"
          id="cover_swatch_grid.title.choose_your_cover"
        />
      </Text>
    </Flex>
  );

  const backButton = (
    <IconButton
      ref={backButtonRef}
      icon={<ConfigurationPanelStepBackIcon />}
      aria-label={intl.formatMessage({
        defaultMessage: 'Back',
        id: 'cover_swatch_overflow_flyout.back',
      })}
      {...(isMobile ? styles.mobileStepBackButton : styles.desktopPanelBackButton)}
      onClick={handleDismiss}
    />
  );

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <Drawer
        blockScrollOnMount={false}
        initialFocusRef={backButtonRef}
        isOpen={isOpen}
        placement="bottom"
        size="full"
        returnFocusOnClose
        onClose={handleDismiss}
        onCloseComplete={handleDrawerCloseComplete}
      >
        <DrawerOverlay {...styles.overlay} />
        <DrawerContent
          className="configuration-panel__root configuration-panel__root--customize-step configuration-panel__root--cover-overflow cover-swatch-overflow-flyout"
          {...styles.content}
          {...(drawerViewportRect ?
            {
              bottom: 'auto',
              height: drawerViewportRect.height,
              left: drawerViewportRect.left,
              maxHeight: drawerViewportRect.height,
              position: 'fixed',
              top: drawerViewportRect.top,
              width: drawerViewportRect.width,
            } :
            {})}
        >
          <Box
            as="header"
            borderBottomWidth={0}
            borderWidth={0}
            className="configuration-panel-header configuration-panel-header--customize-step configuration-panel-header--customize-step--cover"
            flexShrink={0}
            {...styles.header}
          >
            <Flex alignItems="center" gap={4} width="100%">
              {backButton}
              {headerTitle}
            </Flex>
          </Box>
          <Box
            className="configuration-panel-body-wrapper configuration-panel-body-wrapper--customize-step configuration-panel-body-wrapper--customize-step--cover"
            display="flex"
            flex={1}
            flexDirection="column"
            minH={0}
            overflow="hidden"
          >
            {mobileGalleryHeader}
            {panelBody}
          </Box>
        </DrawerContent>
      </Drawer>
    );
  }

  if (typeof document === 'undefined') return null;

  return createPortal(
    <FocusLock initialFocusRef={backButtonRef} returnFocus>
      <Box
        ref={desktopPanelRef}
        aria-labelledby="cover-overflow-flyout-title"
        aria-modal="true"
        bg="white"
        bottom={0}
        className="configuration-selector__desktop-panel cover-swatch-overflow-flyout"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        position="fixed"
        right={0}
        role="dialog"
        top={0}
        width={`${PDP_DESKTOP_CONFIG_PANEL_WIDTH_PX}px`}
        zIndex={1100}
      >
        <Box
          className="configuration-selector-panel__header-desktop configuration-panel-header configuration-panel-header--customize-step configuration-panel-header--customize-step--cover"
          {...styles.desktopPanelHeader}
          gap="16px"
          justifyContent="flex-start"
        >
          {backButton}
          {headerTitle}
        </Box>
        <Box
          className="configuration-panel__root configuration-panel__root--cover-overflow"
          display="flex"
          flex={1}
          flexDirection="column"
          minHeight={0}
          overflow="hidden"
          {...styles.desktopPanel}
        >
          {panelBody}
        </Box>
      </Box>
    </FocusLock>,
    document.body
  );
}

CoverSwatchOverflowFlyout.displayName = 'CoverSwatchOverflowFlyout';

CoverSwatchOverflowFlyout.propTypes = {
  atcDisplayPrice: PropTypes.number,
  committedCover: PropTypes.shape({
    productId: PropTypes.string,
    name: PropTypes.string,
    imageUrl: PropTypes.string,
    variationValues: PropTypes.object,
  }),
  committedSelections: PropTypes.object,
  coverGroups: PropTypes.array.isRequired,
  currency: PropTypes.string,
  isOpen: PropTypes.bool,
  isSimpleConfigurableMaster: PropTypes.bool,
  masterMinPrice: PropTypes.number,
  mobileGalleryLayers: PropTypes.array,
  mobileGalleryPreviewProduct: PropTypes.object,
  mobileGalleryVariationAttributes: PropTypes.object,
  optionListPriceMap: PropTypes.object,
  optionPriceMap: PropTypes.object,
  product: PropTypes.object.isRequired,
  variants: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  onGalleryPreviewChange: PropTypes.func,
  onViewDetails: PropTypes.func,
};

export function buildCommittedCoverMirrorPayload(product, committedCover, variants) {
  const variant = findVariantByProductId(variants, committedCover?.productId);

  return {
    product: null,
    selectedVariationAttributes: variant?.variationValues ?? committedCover?.variationValues ?? {},
    viewDetailsProduct: null,
  };
}

export {findVariantByProductId};
